import { Notation, parseNotation, soundingOf } from '../../music/notation';
import KEYBOARD from './keyboard.generated.json';
import CHORALES from './chorales.generated.json';
import SONATAS from './sonatas.generated.json';
import VARIATIONS from './variations.generated.json';
import WTC from './wtc.generated.json';
import PIANO from './piano.generated.json';

/**
 * The written notation of each piece, from its source edition (scripts/extract_notation.py).
 * The commercial build leaves out the sonata and WTC files, like their scores.
 */
const SOURCES: Record<string, string> = {
  ...(KEYBOARD as Record<string, string>),
  ...(CHORALES as Record<string, string>),
  ...(SONATAS as Record<string, string>),
  ...(VARIATIONS as Record<string, string>),
  ...(WTC as Record<string, string>),
  ...(PIANO as Record<string, string>),
};

const cache = new Map<string, Notation | null>();

export function notationOf(id: string): Notation | null {
  if (cache.has(id)) return cache.get(id)!;
  const src = SOURCES[id];
  const n = src ? parseNotation(src) : null;
  cache.set(id, n);
  return n;
}

export const NOTATION_IDS = Object.keys(SOURCES);

/**
 * The notation of a piece lined up with its playback: each bar moved to where the playback has it
 * (an edition's bar of different length shifts everything after it), then checked note by note.
 * Null when the notation and the sound disagree on more than a few notes: the moving score then
 * falls back to the plain proportional view rather than show a score that is not what plays.
 */
export function alignedNotation(id: string, measures: Array<{ bar: number; start: number; length: number }>, notes: Array<{ onset: number; midi: number }>): Notation | null {
  const n = notationOf(id);
  if (!n || !measures.length) return null;
  const shift = new Array(n.measures.length).fill(0);
  if (n.measures.length === measures.length) n.measures.forEach((m, i) => (shift[i] = measures[i].start - m.start));
  else {
    const byBar = new Map(measures.map((m) => [m.bar, m.start]));
    n.measures.forEach((m, i) => (shift[i] = byBar.has(m.bar) ? byBar.get(m.bar)! - m.start : i > 0 ? shift[i - 1] : 0));
  }
  const moved: Notation = {
    ...n,
    measures: n.measures.map((m, i) => ({ ...m, start: m.start + shift[i] })),
    events: n.events.map((e) => ({ ...e, t: Math.round((e.t + shift[e.m]) * 1e6) / 1e6 })),
    clefs: n.clefs.map((c) => ({ ...c, t: c.t + shift[measureIndex(n, c.t)] })),
    keys: n.keys.map((c) => ({ ...c, t: c.t + shift[measureIndex(n, c.t)] })),
    times: n.times.map((c) => ({ ...c, t: c.t + shift[measureIndex(n, c.t)] })),
    barlines: n.barlines.map((c) => ({ ...c, t: c.t + shift[Math.max(0, measureIndex(n, c.t - 1e-3))] + (c.t - 1e-3 < 0 ? 0 : 0) })),
  };
  const have = new Set(notes.map((x) => `${Math.round(x.onset * 50)}:${x.midi}`));
  const sounding = soundingOf(moved);
  let hit = 0;
  for (const [t, midi] of sounding) if (have.has(`${Math.round(t * 50)}:${midi}`) || have.has(`${Math.round(t * 50) - 1}:${midi}`) || have.has(`${Math.round(t * 50) + 1}:${midi}`)) hit++;
  const ok = notes.length > 0 && hit / Math.max(notes.length, sounding.length) >= 0.97;
  return ok ? moved : null;
}

function measureIndex(n: Notation, t: number): number {
  let lo = 0;
  let hi = n.measures.length - 1;
  while (lo < hi) {
    const m = (lo + hi + 1) >> 1;
    if (n.measures[m].start <= t + 1e-6) lo = m;
    else hi = m - 1;
  }
  return lo;
}
